import React, { useState, useEffect } from 'react';
import { ChevronRight, Copy, ChevronDown, Menu, X } from 'lucide-react';
import { useSelector } from 'react-redux';
import Navbar from '../navigation/Navbar';
import TopNavbar from '../navigation/TopNavbar';

const ApiDocumentation = () => {
  const { txtconnect_ng_user, isVerified } = useSelector((state) => state.user);
  const isAuthenticated = Boolean(txtconnect_ng_user && isVerified);

  const [selectedSection, setSelectedSection] = useState('send-sms-post');
  const [selectedLanguage, setSelectedLanguage] = useState('cUrl');
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar when route/section changes on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Exact sidebar sections from TxtConnect
  const sections = [
    { id: 'intro', name: 'INTRODUCTION', type: 'header' },
    { id: 'reference', name: 'REFERENCE', type: 'header' },
    { id: 'send-sms-post', name: 'Send Single SMS using POST method', method: 'POST' },
    { id: 'send-sms-get', name: 'Send Single SMS using GET method', method: 'GET' },
    { id: 'check-account-balance', name: 'Check Account Balance', method: 'GET' },
    { id: 'check-sent-status-get', name: 'Check SMS Delivery Status using GET method', method: 'GET' },
    { id: 'check-delivery-status-post', name: 'Check SMS Delivery Status using POST method', method: 'POST' },
  ];

  const languages = [
    'cUrl', 'Java', 'JavaScript', 'Node.js',  
    'Python', 'PHP','Laravel',
  ];

  // Dynamic code examples - Updated with TxtConnect endpoints
  const codeExamples = {
    'send-sms-post': {
      cUrl: `curl --include \\
     --request POST \\
     --header "Content-Type: application/json" \\
     --header "Authorization: Bearer <API Key>" \\
     --data-binary "{
    \\"to\\": \\"the recipient Number.\\",
    \\"from\\": \\"the sender name.\\",
    \\"unicode\\": \\"the type of SMS\\",
    \\"sms\\": \\"sms content\\"
}" \\
'https://api.txtconnect.net/dev/api/sms/send'`,
      Java: `// Maven : Add these dependecies to your pom.xml (java6+)
// <dependency>
//     <groupId>org.glassfish.jersey.core</groupId>
//     <artifactId>jersey-client</artifactId>
//     <version>2.8</version>
// </dependency>
// <dependency>
//     <groupId>org.glassfish.jersey.media</groupId>
//     <artifactId>jersey-media-json-jackson</artifactId>
//     <version>2.8</version>
// </dependency>

import javax.ws.rs.client.Client;
import javax.ws.rs.client.ClientBuilder;
import javax.ws.rs.client.Entity;
import javax.ws.rs.core.Response;
import javax.ws.rs.core.MediaType;

Client client = ClientBuilder.newClient();
Entity payload = Entity.json("{  \\"to\\": \\"the recipient Number.\\",  \\"from\\": \\"the sender name.\\",  \\"unicode\\": \\"the type of SMS\\",  \\"sms\\": \\"sms content\\"}");
Response response = client.target("https://api.txtconnect.net/dev/api/sms/send")
  .request(MediaType.APPLICATION_JSON_TYPE)
  .header("Authorization", "Bearer <API Key>")
  .post(payload);

System.out.println("status: " + response.getStatus());
System.out.println("headers: " + response.getHeaders());
System.out.println("body:" + response.readEntity(String.class));`,
      JavaScript: `var request = new XMLHttpRequest();

request.open('POST', 'https://api.txtconnect.net/dev/api/sms/send');

request.setRequestHeader('Content-Type', 'application/json');
request.setRequestHeader('Authorization', 'Bearer <API Key>');

request.onreadystatechange = function () {
  if (this.readyState === 4) {
    //console.log('Status:', this.status);
    //console.log('Headers:', this.getAllResponseHeaders());
    //console.log('Body:', this.responseText);
  }
};

var body = {
  'to': 'the recipient Number.',
  'from': 'the sender name.',
  'unicode': 'the type of SMS',
  'sms': 'sms content'
};

request.send(JSON.stringify(body));`,
      'Node.js': `var request = require('request');

request({
  method: 'POST',
  url: 'https://api.txtconnect.net/dev/api/sms/send',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer <API Key>'
  },
  body: "{  \\"to\\": \\"the recipient Number.\\",  \\"from\\": \\"the sender name.\\",  \\"unicode\\": \\"the type of SMS\\",  \\"sms\\": \\"sms content\\"}"
}, function (error, response, body) {
  //console.log('Status:', response.statusCode);
  //console.log('Headers:', JSON.stringify(response.headers));
  //console.log('Response:', body);
});`,

      Laravel: `$secret = "/* Your API Secret */";
$endpoint = "https://api.txtconnect.net/dev/api/sms/send";

$payload = [
    "from"    => "SenderName",
    "sms"     => "Hello, this is a test message",
    "to"      => "233XXXXXXXXX", // recipient number
    "unicode" => 0,              // 1 for unicode, 0 for non-unicode
];

$response = Http::withBearerToken($secret)
    ->withHeaders([
        'Content-Type'  => 'application/json',
        'Cache-Control' => 'no-cache',
    ])
    ->post($endpoint, $payload);

$result = $response->json(); // Now you can access the API response as an array

print_r($result);
`,

      Perl: `$ENV{'PERL_LWP_SSL_VERIFY_HOSTNAME'} = 0;
use LWP::UserAgent;
use strict;
use warnings;
use 5.010;
use Cpanel::JSON::XS qw(encode_json decode_json);

my $ua   = LWP::UserAgent->new;
my $data = '{  "to": "the recipient Number.",  "from": "the sender name.",  "unicode": "the type of SMS",  "sms": "sms content"}';

$ua->default_header("Content-Type" => "application/json");
$ua->default_header("Authorization" => "Bearer <API Key>");

my $response = $ua->post("https://api.txtconnect.net/dev/api/sms/send", Content => $data);

print $response->as_string;`,
      Python: `import urllib2
import json

url = 'https://api.txtconnect.net/dev/api/sms/send'
headers = {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer <API Key>'
}
data = {
    'to': 'the recipient Number.',
    'from': 'the sender name.',
    'unicode': 'the type of SMS',
    'sms': 'sms content'
}

req = urllib2.Request(url, json.dumps(data), headers)
response = urllib2.urlopen(req)
print response.read()`,
      PHP: `<?php
$ch = curl_init();

curl_setopt($ch, CURLOPT_URL, "https://api.txtconnect.net/dev/api/sms/send");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, TRUE);
curl_setopt($ch, CURLOPT_HEADER, FALSE);

curl_setopt($ch, CURLOPT_POST, TRUE);

curl_setopt($ch, CURLOPT_POSTFIELDS, "{
  \\"to\\": \\"the recipient Number.\\",
  \\"from\\": \\"the sender name.\\",
  \\"unicode\\": \\"the type of SMS\\",
  \\"sms\\": \\"sms content\\"
}");

curl_setopt($ch, CURLOPT_HTTPHEADER, array(
  "Content-Type: application/json",
  "Authorization: Bearer <API Key>"
));

$response = curl_exec($ch);
curl_close($ch);

var_dump($response);`,
      Ruby: `require 'net/http'
require 'json'

uri = URI('https://api.txtconnect.net/dev/api/sms/send')
http = Net::HTTP.new(uri.host, uri.port)
http.use_ssl = true

request = Net::HTTP::Post.new(uri.path)
request['Content-Type'] = 'application/json'
request['Authorization'] = 'Bearer <API Key>'
request.body = {
  to: 'the recipient Number.',
  from: 'the sender name.',
  unicode: 'the type of SMS',
  sms: 'sms content'
}.to_json

response = http.request(request)
puts response.body`,
      Go: `package main

import (
	"bytes"
	"fmt"
	"io/ioutil"
	"net/http"
)

func main() {
	client := &http.Client{}

	body := []byte("{\\n  \\"to\\": \\"the recipient Number.\\",\\n  \\"from\\": \\"the sender name.\\",\\n  \\"unicode\\": \\"the type of SMS\\",\\n  \\"sms\\": \\"sms content\\"\\n}")

	req, _ := http.NewRequest("POST", "https://api.txtconnect.net/dev/api/sms/send", bytes.NewBuffer(body))

	req.Header.Add("Content-Type", "application/json")
	req.Header.Add("Authorization", "Bearer <API Key>")

	resp, err := client.Do(req)

	if err != nil {
		fmt.Println("Errored when sending request to the server")
		return
	}

	defer resp.Body.Close()
	resp_body, _ := ioutil.ReadAll(resp.Body)

	fmt.Println(resp.Status)
	fmt.Println(string(resp_body))
}`,
      'C#': `using System;
using System.Net.Http;
using System.Text;

class Program
{
    static async System.Threading.Tasks.Task Main()
    {
        using (var client = new HttpClient())
        {
            client.BaseAddress = new Uri("https://api.txtconnect.net/dev/api/sms/");
            
            var content = new StringContent(
                "{\\"to\\": \\"the recipient Number.\\",\\"from\\": \\"the sender name.\\",\\"unicode\\": \\"the type of SMS\\",\\"sms\\": \\"sms content\\"}",
                Encoding.UTF8,
                "application/json");
                
            client.DefaultRequestHeaders.Authorization = 
                new System.Net.Http.Headers.AuthenticationHeaderValue("Bearer", "<API Key>");
            
            var response = await client.PostAsync("send", content);
            var result = await response.Content.ReadAsStringAsync();
            Console.WriteLine(result);
        }
    }
}`,
      'Visual Basic': `Dim request = TryCast(System.Net.WebRequest.Create("https://api.txtconnect.net/dev/api/sms/send"), System.Net.HttpWebRequest)

request.Method = "POST"

request.ContentType = "application/json"
request.Headers.Add("authorization", "Bearer <API Key>")

Using writer = New System.IO.StreamWriter(request.GetRequestStream())
  Dim byteArray As Byte() = System.Text.Encoding.UTF8.GetBytes("{
  \\"to\\": \\"the recipient Number.\\",
  \\"from\\": \\"the sender name.\\",
  \\"unicode\\": \\"the type of SMS\\",
  \\"sms\\": \\"sms content\\"
}")
  request.ContentLength = byteArray.Length
  writer.Write(byteArray)
  writer.Close()
End Using
Dim responseContent As String
Using response = TryCast(request.GetResponse(), System.Net.HttpWebResponse)
  Using reader = New System.IO.StreamReader(response.GetResponseStream())
    responseContent = reader.ReadToEnd()
  End Using
End Using`,
      Groovy: `import groovyx.net.http.RESTClient
import static groovyx.net.http.ContentType.JSON
import groovy.json.JsonSlurper
import groovy.json.JsonOutput

@Grab (group = 'org.codehaus.groovy.modules.http-builder', module = 'http-builder', version = '0.5.0')
def client = new RESTClient("https://api.txtconnect.net/dev/api/sms")

def headers = [:]
headers."Content-Type" = "application/json"
headers."Authorization" = "Bearer <API Key>"

def jsonObj = [
    to: "the recipient Number.",
    from: "the sender name.",
    unicode: "the type of SMS",
    sms: "sms content"
]

response = client.post( path : "/send",
                        body : jsonObj,
                        headers: headers,
                        contentType : JSON )

println("Status:" + response.status)
println("Body:\\n" + JsonOutput.prettyPrint(JsonOutput.toJson(response.data)))`,
      'Objective-C': `NSURL *URL = [NSURL URLWithString:@"https://api.txtconnect.net/dev/api/sms/send"];

NSMutableURLRequest *request = [NSMutableURLRequest requestWithURL:URL];
[request setHTTPMethod:@"POST"];

[request setValue:@"application/json" forHTTPHeaderField:@"Content-Type"];
[request setValue:@"Bearer <API Key>" forHTTPHeaderField:@"Authorization"];

[request setHTTPBody:[@"{\\n  \\"to\\": \\"the recipient Number.\\",\\n  \\"from\\": \\"the sender name.\\",\\n  \\"unicode\\": \\"the type of SMS\\",\\n  \\"sms\\": \\"sms content\\"\\n}" dataUsingEncoding:NSUTF8StringEncoding]];

NSURLSession *session = [NSURLSession sharedSession];
NSURLSessionDataTask *task = [session dataTaskWithRequest:request
                                        completionHandler:
                              ^(NSData *data, NSURLResponse *response, NSError *error) {
                                  if (error) {
                                      NSLog(@"Error: %@", error);
                                      return;
                                  }
                                  
                                  if ([response isKindOfClass:[NSHTTPURLResponse class]]) {
                                      NSLog(@"Response HTTP Status code: %ld\\n", (long)[(NSHTTPURLResponse *)response statusCode]);
                                  }
                                  
                                  NSString* body = [[NSString alloc] initWithData:data encoding:NSUTF8StringEncoding];
                                  NSLog(@"Response Body:\\n%@\\n", body);
                              }];
[task resume];`,
      Swift: `import Foundation

let url = URL(string: "https://api.txtconnect.net/dev/api/sms/send")!
var request = URLRequest(url: url)
request.httpMethod = "POST"
request.addValue("application/json", forHTTPHeaderField: "Content-Type")
request.addValue("Bearer <API Key>", forHTTPHeaderField: "Authorization")

let json: [String: Any] = [
    "to": "the recipient Number.",
    "from": "the sender name.",
    "unicode": "the type of SMS",
    "sms": "sms content"
]

request.httpBody = try? JSONSerialization.data(withJSONObject: json)

let task = URLSession.shared.dataTask(with: request) { data, response, error in
    if let error = error {
        print("Error: \\(error)")
        return
    }
    
    if let httpResponse = response as? HTTPURLResponse {
        print("Status code: \\(httpResponse.statusCode)")
    }
    
    if let data = data, let body = String(data: data, encoding: .utf8) {
        print("Response: \\(body)")
    }
}

task.resume()`
    },
    'send-sms-get': {

          Laravel: ` <?php

use Illuminate\Support\Facades\Http;

$secret = "/* Your API Secret */";

$to      = "233XXXXXXXXX";
$from    = "SenderName";
$unicode = 0;
$sms     = "Hello, this is a test message";

$endpoint = "https://api.txtconnect.net/v1/send/{$to}/{$from}/{$unicode}/" . urlencode($sms);

$response = Http::withBearerToken($secret)
    ->withHeaders([
        'Cache-Control' => 'no-cache',
        'request-id'    => $transaction_id,
    ])
    ->get($endpoint);

$result = $response->json();

print_r($result);`,


      cUrl: `curl --include \\
     --header "Authorization: Bearer <API Key>" \\
  'https://api.txtconnect.net/dev/api/sms/send/to/from/unicode/sms'`,
      Java: `// Maven : Add these dependecies to your pom.xml (java6+)
// <dependency>
//     <groupId>org.glassfish.jersey.core</groupId>
//     <artifactId>jersey-client</artifactId>
//     <version>2.8</version>
// </dependency>
// <dependency>
//     <groupId>org.glassfish.jersey.media</groupId>
//     <artifactId>jersey-media-json-jackson</artifactId>
//     <version>2.8</version>
// </dependency>

import javax.ws.rs.client.Client;
import javax.ws.rs.client.ClientBuilder;
import javax.ws.rs.core.Response;
import javax.ws.rs.core.MediaType;

Client client = ClientBuilder.newClient();
Response response = client.target("https://api.txtconnect.net/dev/api/sms/send/to/from/unicode/sms")
  .request(MediaType.TEXT_PLAIN_TYPE)
  .header("Authorization", "Bearer <API Key>")
  .get();

System.out.println("status: " + response.getStatus());
System.out.println("headers: " + response.getHeaders());
System.out.println("body:" + response.readEntity(String.class));`,
      JavaScript: `var request = new XMLHttpRequest();

request.open('GET', 'https://api.txtconnect.net/dev/api/sms/send/to/from/unicode/sms');

request.setRequestHeader('Authorization', 'Bearer <API Key>');

request.onreadystatechange = function () {
  if (this.readyState === 4) {
    //console.log('Status:', this.status);
    //console.log('Headers:', this.getAllResponseHeaders());
    //console.log('Body:', this.responseText);
  }
};

request.send();`,
      'Node.js': `var request = require('request');

request({
  method: 'GET',
  url: 'https://api.txtconnect.net/dev/api/sms/send/to/from/unicode/sms',
  headers: {
    'Authorization': 'Bearer <API Key>'
  }}, function (error, response, body) {
  //console.log('Status:', response.statusCode);
  //console.log('Headers:', JSON.stringify(response.headers));
  //console.log('Response:', body);
});`,

  

      Perl: `$ENV{'PERL_LWP_SSL_VERIFY_HOSTNAME'} = 0;
use LWP::UserAgent;
use strict;
use warnings;
use 5.010;
use Cpanel::JSON::XS qw(encode_json decode_json);

my $ua   = LWP::UserAgent->new;

$ua->default_header("Authorization" => "Bearer <API Key>");

my $response = $ua->get("https://api.txtconnect.net/dev/api/sms/send/to/from/unicode/sms");

print $response->as_string;`,



      Python: `import urllib2

headers = {
  'Authorization': 'Bearer <API Key>'
}
request = urllib2.Request('https://api.txtconnect.net/dev/api/sms/send/to/from/unicode/sms', headers=headers)

response_body = urllib2.urlopen(request).read()
print response_body`,
      PHP: `<?php
$ch = curl_init();

curl_setopt($ch, CURLOPT_URL, "https://api.txtconnect.net/dev/api/sms/send/to/from/unicode/sms");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, TRUE);
curl_setopt($ch, CURLOPT_HEADER, FALSE);

curl_setopt($ch, CURLOPT_HTTPHEADER, array(
  "Authorization: Bearer <API Key>"
));

$response = curl_exec($ch);
curl_close($ch);

var_dump($response);`,
      Ruby: `require 'rubygems' if RUBY_VERSION < '1.9'
require 'rest_client'

headers = {
  :authorization => 'Bearer <API Key>'
}

response = RestClient.get 'https://api.txtconnect.net/dev/api/sms/send/to/from/unicode/sms', headers
puts response`,
      Go: `package main

import (
	"fmt"
	"io/ioutil"
	"net/http"
)

func main() {
	client := &http.Client{}

	req, _ := http.NewRequest("GET", "https://api.txtconnect.net/dev/api/sms/send/to/from/unicode/sms", nil)

	req.Header.Add("Authorization", "Bearer <API Key>")

	resp, err := client.Do(req)

	if err != nil {
		fmt.Println("Errored when sending request to the server")
		return
	}

	defer resp.Body.Close()
	resp_body, _ := ioutil.ReadAll(resp.Body)

	fmt.Println(resp.Status)
	fmt.Println(string(resp_body))
}`,
      'C#': `//Common testing requirement. If you are consuming an API in a sandbox/test region, uncomment this line of code ONLY for non production uses.
//System.Net.ServicePointManager.ServerCertificateValidationCallback = delegate { return true; };

//Be sure to run "Install-Package Microsoft.Net.Http" from your nuget command line.
using System;
using System.Net.Http;

var baseAddress = new Uri("https://api.txtconnect.net/dev/api/sms/");

using (var httpClient = new HttpClient{ BaseAddress = baseAddress })
{
  httpClient.DefaultRequestHeaders.TryAddWithoutValidation("authorization", "Bearer <API Key>");
  
  using(var response = await httpClient.GetAsync("send/{to}/{from}/{unicode}/{sms}"))
  {
        string responseData = await response.Content.ReadAsStringAsync();
  }
}`,
      'Visual Basic': `Dim request = TryCast(System.Net.WebRequest.Create("https://api.txtconnect.net/dev/api/sms/send/to/from/unicode/sms"), System.Net.HttpWebRequest)

request.Method = "GET"

request.Headers.Add("authorization", "Bearer <API Key>")

request.ContentLength = 0
Dim responseContent As String
Using response = TryCast(request.GetResponse(), System.Net.HttpWebResponse)
  Using reader = New System.IO.StreamReader(response.GetResponseStream())
    responseContent = reader.ReadToEnd()
  End Using
End Using`,
      Groovy: `import groovyx.net.http.RESTClient
import static groovyx.net.http.ContentType.JSON
import groovy.json.JsonSlurper
import groovy.json.JsonOutput

@Grab (group = 'org.codehaus.groovy.modules.http-builder', module = 'http-builder', version = '0.5.0')
def client = new RESTClient("https://api.txtconnect.net/dev/api/sms")

def emptyHeaders = [:]
emptyHeaders."Authorization" = "Bearer <API Key>"

response = client.get( path : "/send/{to}/{from}/{unicode}/{sms}", headers: emptyHeaders )

println("Status:" + response.status)

if (response.data) {
  println("Content Type: " + response.contentType)
  println("Body:\\n" + JsonOutput.prettyPrint(JsonOutput.toJson(response.data)))
}`,
      'Objective-C': `NSURL *URL = [NSURL URLWithString:@"https://api.txtconnect.net/dev/api/sms/send/to/from/unicode/sms"];

NSMutableURLRequest *request = [NSMutableURLRequest requestWithURL:URL];
[request setHTTPMethod:@"GET"];

[request setValue:@"Bearer <API Key>" forHTTPHeaderField:@"Authorization"];

NSURLSession *session = [NSURLSession sharedSession];
NSURLSessionDataTask *task = [session dataTaskWithRequest:request
                                        completionHandler:
                              ^(NSData *data, NSURLResponse *response, NSError *error) {
                                  if (error) {
                                      NSLog(@"Error: %@", error);
                                      return;
                                  }
                                  
                                  if ([response isKindOfClass:[NSHTTPURLResponse class]]) {
                                      NSLog(@"Response HTTP Status code: %ld\\n", (long)[(NSHTTPURLResponse *)response statusCode]);
                                  }
                                  
                                  NSString* body = [[NSString alloc] initWithData:data encoding:NSUTF8StringEncoding];
                                  NSLog(@"Response Body:\\n%@\\n", body);
                              }];
[task resume];`,
      Swift: `import Foundation

let url = URL(string: "https://api.txtconnect.net/dev/api/sms/send/to/from/unicode/sms")!
var request = URLRequest(url: url)
request.addValue("Bearer <API Key>", forHTTPHeaderField: "Authorization")

let task = URLSession.shared.dataTask(with: request) { data, response, error in
    if let error = error {
        print("Error: \\(error)")
        return
    }
    
    if let httpResponse = response as? HTTPURLResponse {
        print("Status code: \\(httpResponse.statusCode)")
    }
    
    if let data = data, let body = String(data: data, encoding: .utf8) {
        print("Response: \\(body)")
    }
}

task.resume()`
    },
    'check-account-balance': {
      cUrl: `curl --include \\
     --header "Authorization: Bearer <API Key>" \\
  'https://api.txtconnect.net/dev/api/sms/checkbalance'`,
      Java: `// Maven : Add these dependecies to your pom.xml (java6+)
// <dependency>
//     <groupId>org.glassfish.jersey.core</groupId>
//     <artifactId>jersey-client</artifactId>
//     <version>2.8</version>
// </dependency>
// <dependency>
//     <groupId>org.glassfish.jersey.media</groupId>
//     <artifactId>jersey-media-json-jackson</artifactId>
//     <version>2.8</version>
// </dependency>

import javax.ws.rs.client.Client;
import javax.ws.rs.client.ClientBuilder;
import javax.ws.rs.core.Response;
import javax.ws.rs.core.MediaType;

Client client = ClientBuilder.newClient();
Response response = client.target("https://api.txtconnect.net/dev/api/sms/checkbalance")
  .request(MediaType.TEXT_PLAIN_TYPE)
  .header("Authorization", "Bearer <API Key>")
  .get();

System.out.println("status: " + response.getStatus());
System.out.println("headers: " + response.getHeaders());
System.out.println("body:" + response.readEntity(String.class));`,
      JavaScript: `var request = new XMLHttpRequest();

request.open('GET', 'https://api.txtconnect.net/dev/api/sms/checkbalance');

request.setRequestHeader('Authorization', 'Bearer <API Key>');

request.onreadystatechange = function () {
  if (this.readyState === 4) {
    //console.log('Status:', this.status);
    //console.log('Headers:', this.getAllResponseHeaders());
    //console.log('Body:', this.responseText);
  }
};

request.send();`,
      'Node.js': `var request = require('request');

request({
  method: 'GET',
  url: 'https://api.txtconnect.net/dev/api/sms/checkbalance',
  headers: {
    'Authorization': 'Bearer <API Key>'
  }}, function (error, response, body) {
  //console.log('Status:', response.statusCode);
  //console.log('Headers:', JSON.stringify(response.headers));
  //console.log('Response:', body);
});`,
      Perl: `$ENV{'PERL_LWP_SSL_VERIFY_HOSTNAME'} = 0;
use LWP::UserAgent;
use strict;
use warnings;
use 5.010;
use Cpanel::JSON::XS qw(encode_json decode_json);

my $ua   = LWP::UserAgent->new;

$ua->default_header("Authorization" => "Bearer <API Key>");

my $response = $ua->get("https://api.txtconnect.net/dev/api/sms/checkbalance");

print $response->as_string;`,
      Python: `import urllib2

headers = {
  'Authorization': 'Bearer <API Key>'
}
request = urllib2.Request('https://api.txtconnect.net/dev/api/sms/checkbalance', headers=headers)

response_body = urllib2.urlopen(request).read()
print response_body`,
      PHP: `<?php
$ch = curl_init();

curl_setopt($ch, CURLOPT_URL, "https://api.txtconnect.net/dev/api/sms/checkbalance");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, TRUE);
curl_setopt($ch, CURLOPT_HEADER, FALSE);

curl_setopt($ch, CURLOPT_HTTPHEADER, array(
  "Authorization: Bearer <API Key>"
));

$response = curl_exec($ch);
curl_close($ch);

var_dump($response);`,
      Ruby: `require 'rubygems' if RUBY_VERSION < '1.9'
require 'rest_client'

headers = {
  :authorization => 'Bearer <API Key>'
}

response = RestClient.get 'https://api.txtconnect.net/dev/api/sms/checkbalance', headers
puts response`,
      Go: `package main

import (
	"fmt"
	"io/ioutil"
	"net/http"
)

func main() {
	client := &http.Client{}

	req, _ := http.NewRequest("GET", "https://api.txtconnect.net/dev/api/sms/checkbalance", nil)

	req.Header.Add("Authorization", "Bearer <API Key>")

	resp, err := client.Do(req)

	if err != nil {
		fmt.Println("Errored when sending request to the server")
		return
	}

	defer resp.Body.Close()
	resp_body, _ := ioutil.ReadAll(resp.Body)

	fmt.Println(resp.Status)
	fmt.Println(string(resp_body))
}`,
      'C#': `//Common testing requirement. If you are consuming an API in a sandbox/test region, uncomment this line of code ONLY for non production uses.
//System.Net.ServicePointManager.ServerCertificateValidationCallback = delegate { return true; };

//Be sure to run "Install-Package Microsoft.Net.Http" from your nuget command line.
using System;
using System.Net.Http;

var baseAddress = new Uri("https://api.txtconnect.net/dev/api/sms/");

using (var httpClient = new HttpClient{ BaseAddress = baseAddress })
{
  httpClient.DefaultRequestHeaders.TryAddWithoutValidation("authorization", "Bearer <API Key>");
  
  using(var response = await httpClient.GetAsync("checkbalance"))
  {
        string responseData = await response.Content.ReadAsStringAsync();
  }
}`,
      'Visual Basic': `Dim request = TryCast(System.Net.WebRequest.Create("https://api.txtconnect.net/dev/api/sms/checkbalance"), System.Net.HttpWebRequest)

request.Method = "GET"

request.Headers.Add("authorization", "Bearer <API Key>")

request.ContentLength = 0
Dim responseContent As String
Using response = TryCast(request.GetResponse(), System.Net.HttpWebResponse)
  Using reader = New System.IO.StreamReader(response.GetResponseStream())
    responseContent = reader.ReadToEnd()
  End Using
End Using`,
      Groovy: `import groovyx.net.http.RESTClient
import static groovyx.net.http.ContentType.JSON
import groovy.json.JsonSlurper
import groovy.json.JsonOutput

@Grab (group = 'org.codehaus.groovy.modules.http-builder', module = 'http-builder', version = '0.5.0')
def client = new RESTClient("https://api.txtconnect.net/dev/api/sms")

def emptyHeaders = [:]
emptyHeaders."Authorization" = "Bearer <API Key>"

response = client.get( path : "/checkbalance", headers: emptyHeaders )

println("Status:" + response.status)

if (response.data) {
  println("Content Type: " + response.contentType)
  println("Body:\\n" + JsonOutput.prettyPrint(JsonOutput.toJson(response.data)))
}`,
      'Objective-C': `NSURL *URL = [NSURL URLWithString:@"https://api.txtconnect.net/dev/api/sms/checkbalance"];

NSMutableURLRequest *request = [NSMutableURLRequest requestWithURL:URL];
[request setHTTPMethod:@"GET"];

[request setValue:@"Bearer <API Key>" forHTTPHeaderField:@"Authorization"];

NSURLSession *session = [NSURLSession sharedSession];
NSURLSessionDataTask *task = [session dataTaskWithRequest:request
                                        completionHandler:
                              ^(NSData *data, NSURLResponse *response, NSError *error) {
                                  if (error) {
                                      NSLog(@"Error: %@", error);
                                      return;
                                  }
                                  
                                  if ([response isKindOfClass:[NSHTTPURLResponse class]]) {
                                      NSLog(@"Response HTTP Status code: %ld\\n", (long)[(NSHTTPURLResponse *)response statusCode]);
                                  }
                                  
                                  NSString* body = [[NSString alloc] initWithData:data encoding:NSUTF8StringEncoding];
                                  NSLog(@"Response Body:\\n%@\\n", body);
                              }];
[task resume];`,
      Swift: `import Foundation

let url = URL(string: "https://api.txtconnect.net/dev/api/sms/checkbalance")!
var request = URLRequest(url: url)
request.addValue("Bearer <API Key>", forHTTPHeaderField: "Authorization")

let task = URLSession.shared.dataTask(with: request) { data, response, error in
    if let error = error {
        print("Error: \\(error)")
        return
    }
    
    if let httpResponse = response as? HTTPURLResponse {
        print("Status code: \\(httpResponse.statusCode)")
    }
    
    if let data = data, let body = String(data: data, encoding: .utf8) {
        print("Response: \\(body)")
    }
}

task.resume()`
    },
    'check-sent-status-get': {
      cUrl: `curl --include \\
     --header "Authorization: Bearer <API Key>" \\
  'https://api.txtconnect.net/dev/api/sms/getstatus/msgId'`,
      Java: `// Maven : Add these dependecies to your pom.xml (java6+)
// <dependency>
//     <groupId>org.glassfish.jersey.core</groupId>
//     <artifactId>jersey-client</artifactId>
//     <version>2.8</version>
// </dependency>
// <dependency>
//     <groupId>org.glassfish.jersey.media</groupId>
//     <artifactId>jersey-media-json-jackson</artifactId>
//     <version>2.8</version>
// </dependency>

import javax.ws.rs.client.Client;
import javax.ws.rs.client.ClientBuilder;
import javax.ws.rs.core.Response;
import javax.ws.rs.core.MediaType;

Client client = ClientBuilder.newClient();
Response response = client.target("https://api.txtconnect.net/dev/api/sms/getstatus/msgId")
  .request(MediaType.TEXT_PLAIN_TYPE)
  .header("Authorization", "Bearer <API Key>")
  .get();

System.out.println("status: " + response.getStatus());
System.out.println("headers: " + response.getHeaders());
System.out.println("body:" + response.readEntity(String.class));`,
      JavaScript: `var request = new XMLHttpRequest();

request.open('GET', 'https://api.txtconnect.net/dev/api/sms/getstatus/msgId');

request.setRequestHeader('Authorization', 'Bearer <API Key>');

request.onreadystatechange = function () {
  if (this.readyState === 4) {
    //console.log('Status:', this.status);
    //console.log('Headers:', this.getAllResponseHeaders());
    //console.log('Body:', this.responseText);
  }
};

request.send();`,
      'Node.js': `var request = require('request');

request({
  method: 'GET',
  url: 'https://api.txtconnect.net/dev/api/sms/getstatus/msgId',
  headers: {
    'Authorization': 'Bearer <API Key>'
  }}, function (error, response, body) {
  //console.log('Status:', response.statusCode);
  //console.log('Headers:', JSON.stringify(response.headers));
  //console.log('Response:', body);
});`,
      Perl: `$ENV{'PERL_LWP_SSL_VERIFY_HOSTNAME'} = 0;
use LWP::UserAgent;
use strict;
use warnings;
use 5.010;
use Cpanel::JSON::XS qw(encode_json decode_json);

my $ua   = LWP::UserAgent->new;

$ua->default_header("Authorization" => "Bearer <API Key>");

my $response = $ua->get("https://api.txtconnect.net/dev/api/sms/getstatus/msgId");

print $response->as_string;`,
      Python: `import urllib2

headers = {
  'Authorization': 'Bearer <API Key>'
}
request = urllib2.Request('https://api.txtconnect.net/dev/api/sms/getstatus/msgId', headers=headers)

response_body = urllib2.urlopen(request).read()
print response_body`,
      PHP: `<?php
$ch = curl_init();

curl_setopt($ch, CURLOPT_URL, "https://api.txtconnect.net/dev/api/sms/getstatus/msgId");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, TRUE);
curl_setopt($ch, CURLOPT_HEADER, FALSE);

curl_setopt($ch, CURLOPT_HTTPHEADER, array(
  "Authorization: Bearer <API Key>"
));

$response = curl_exec($ch);
curl_close($ch);

var_dump($response);`,
      Ruby: `require 'rubygems' if RUBY_VERSION < '1.9'
require 'rest_client'

headers = {
  :authorization => 'Bearer <API Key>'
}

response = RestClient.get 'https://api.txtconnect.net/dev/api/sms/getstatus/msgId', headers
puts response`,
      Go: `package main

import (
	"fmt"
	"io/ioutil"
	"net/http"
)

func main() {
	client := &http.Client{}

	req, _ := http.NewRequest("GET", "https://api.txtconnect.net/dev/api/sms/getstatus/msgId", nil)

	req.Header.Add("Authorization", "Bearer <API Key>")

	resp, err := client.Do(req)

	if err != nil {
		fmt.Println("Errored when sending request to the server")
		return
	}

	defer resp.Body.Close()
	resp_body, _ := ioutil.ReadAll(resp.Body)

	fmt.Println(resp.Status)
	fmt.Println(string(resp_body))
}`,
      'C#': `//Common testing requirement. If you are consuming an API in a sandbox/test region, uncomment this line of code ONLY for non production uses.
//System.Net.ServicePointManager.ServerCertificateValidationCallback = delegate { return true; };

//Be sure to run "Install-Package Microsoft.Net.Http" from your nuget command line.
using System;
using System.Net.Http;

var baseAddress = new Uri("https://api.txtconnect.net/dev/api/sms/");

using (var httpClient = new HttpClient{ BaseAddress = baseAddress })
{
  httpClient.DefaultRequestHeaders.TryAddWithoutValidation("authorization", "Bearer <API Key>");
  
  using(var response = await httpClient.GetAsync("getstatus/{msgId}"))
  {
        string responseData = await response.Content.ReadAsStringAsync();
  }
}`,
      'Visual Basic': `Dim request = TryCast(System.Net.WebRequest.Create("https://api.txtconnect.net/dev/api/sms/getstatus/msgId"), System.Net.HttpWebRequest)

request.Method = "GET"

request.Headers.Add("authorization", "Bearer <API Key>")

request.ContentLength = 0
Dim responseContent As String
Using response = TryCast(request.GetResponse(), System.Net.HttpWebResponse)
  Using reader = New System.IO.StreamReader(response.GetResponseStream())
    responseContent = reader.ReadToEnd()
  End Using
End Using`,
      Groovy: `import groovyx.net.http.RESTClient
import static groovyx.net.http.ContentType.JSON
import groovy.json.JsonSlurper
import groovy.json.JsonOutput

@Grab (group = 'org.codehaus.groovy.modules.http-builder', module = 'http-builder', version = '0.5.0')
def client = new RESTClient("https://api.txtconnect.net/dev/api/sms")

def emptyHeaders = [:]
emptyHeaders."Authorization" = "Bearer <API Key>"

response = client.get( path : "/getstatus/{msgId}", headers: emptyHeaders )

println("Status:" + response.status)

if (response.data) {
  println("Content Type: " + response.contentType)
  println("Body:\\n" + JsonOutput.prettyPrint(JsonOutput.toJson(response.data)))
}`,
      'Objective-C': `NSURL *URL = [NSURL URLWithString:@"https://api.txtconnect.net/dev/api/sms/getstatus/msgId"];

NSMutableURLRequest *request = [NSMutableURLRequest requestWithURL:URL];
[request setHTTPMethod:@"GET"];

[request setValue:@"Bearer <API Key>" forHTTPHeaderField:@"Authorization"];

NSURLSession *session = [NSURLSession sharedSession];
NSURLSessionDataTask *task = [session dataTaskWithRequest:request
                                        completionHandler:
                              ^(NSData *data, NSURLResponse *response, NSError *error) {
                                  if (error) {
                                      NSLog(@"Error: %@", error);
                                      return;
                                  }
                                  
                                  if ([response isKindOfClass:[NSHTTPURLResponse class]]) {
                                      NSLog(@"Response HTTP Status code: %ld\\n", (long)[(NSHTTPURLResponse *)response statusCode]);
                                  }
                                  
                                  NSString* body = [[NSString alloc] initWithData:data encoding:NSUTF8StringEncoding];
                                  NSLog(@"Response Body:\\n%@\\n", body);
                              }];
[task resume];`,
      Swift: `import Foundation

let url = URL(string: "https://api.txtconnect.net/dev/api/sms/getstatus/msgId")!
var request = URLRequest(url: url)
request.addValue("Bearer <API Key>", forHTTPHeaderField: "Authorization")

let task = URLSession.shared.dataTask(with: request) { data, response, error in
    if let error = error {
        print("Error: \\(error)")
        return
    }
    
    if let httpResponse = response as? HTTPURLResponse {
        print("Status code: \\(httpResponse.statusCode)")
    }
    
    if let data = data, let body = String(data: data, encoding: .utf8) {
        print("Response: \\(body)")
    }
}

task.resume()`
    },
    'check-delivery-status-post': {
      cUrl: `curl --include \\
     --request POST \\
     --header "Content-Type: application/json" \\
     --header "Authorization: Bearer <API Key>" \\
     --data-binary "{
    \\"messageId\\": \\"message_id\\",
}" \\
'https://api.txtconnect.net/dev/api/sms/getstatus'`,
      Java: `// Maven : Add these dependecies to your pom.xml (java6+)
// <dependency>
//     <groupId>org.glassfish.jersey.core</groupId>
//     <artifactId>jersey-client</artifactId>
//     <version>2.8</version>
// </dependency>
// <dependency>
//     <groupId>org.glassfish.jersey.media</groupId>
//     <artifactId>jersey-media-json-jackson</artifactId>
//     <version>2.8</version>
// </dependency>

import javax.ws.rs.client.Client;
import javax.ws.rs.client.ClientBuilder;
import javax.ws.rs.client.Entity;
import javax.ws.rs.core.Response;
import javax.ws.rs.core.MediaType;

Client client = ClientBuilder.newClient();
Entity payload = Entity.json("{
    \\"messageId\\": \\"message_id\\",
}");
Response response = client.target("https://api.txtconnect.net/dev/api/sms/getstatus")
  .request(MediaType.APPLICATION_JSON_TYPE)
  .header("Authorization", "Bearer <API Key>")
  .post(payload);

System.out.println("status: " + response.getStatus());
System.out.println("headers: " + response.getHeaders());
System.out.println("body:" + response.readEntity(String.class));`,
      JavaScript: `var request = new XMLHttpRequest();

request.open('POST', 'https://api.txtconnect.net/dev/api/sms/getstatus');

request.setRequestHeader('Content-Type', 'application/json');
request.setRequestHeader('Authorization', 'Bearer <API Key>');

request.onreadystatechange = function () {
  if (this.readyState === 4) {
    //console.log('Status:', this.status);
    //console.log('Headers:', this.getAllResponseHeaders());
    //console.log('Body:', this.responseText);
  }
};

var body = {
    'messageId': 'message_id',
};

request.send(JSON.stringify(body));`,
      'Node.js': `var request = require('request');

request({
  method: 'POST',
  url: 'https://api.txtconnect.net/dev/api/sms/getstatus',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer <API Key>'
  },
  body: "{
    \\"messageId\\": \\"message_id\\",
}"
}, function (error, response, body) {
  //console.log('Status:', response.statusCode);
  //console.log('Headers:', JSON.stringify(response.headers));
  //console.log('Response:', body);
});`,
      Perl: `$ENV{'PERL_LWP_SSL_VERIFY_HOSTNAME'} = 0;
use LWP::UserAgent;
use strict;
use warnings;
use 5.010;
use Cpanel::JSON::XS qw(encode_json decode_json);

my $ua   = LWP::UserAgent->new;
my $data = '{
    "messageId": "message_id",
}';

$ua->default_header("Content-Type" => "application/json");
$ua->default_header("Authorization" => "Bearer <API Key>");

my $response = $ua->post("https://api.txtconnect.net/dev/api/sms/getstatus", Content => $data);

print $response->as_string;`,
      Python: `import urllib2
import json

url = 'https://api.txtconnect.net/dev/api/sms/getstatus'
headers = {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer <API Key>'
}
data = {
    'messageId': 'message_id'
}

req = urllib2.Request(url, json.dumps(data), headers)
response = urllib2.urlopen(req)
print response.read()`,
      PHP: `<?php
$ch = curl_init();

curl_setopt($ch, CURLOPT_URL, "https://api.txtconnect.net/dev/api/sms/getstatus");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, TRUE);
curl_setopt($ch, CURLOPT_HEADER, FALSE);

curl_setopt($ch, CURLOPT_POST, TRUE);

curl_setopt($ch, CURLOPT_POSTFIELDS, "{
    \\"messageId\\": \\"message_id\\",
}");

curl_setopt($ch, CURLOPT_HTTPHEADER, array(
  "Content-Type: application/json",
  "Authorization: Bearer <API Key>"
));

$response = curl_exec($ch);
curl_close($ch);

var_dump($response);`,
      Ruby: `require 'net/http'
require 'json'

uri = URI('https://api.txtconnect.net/dev/api/sms/getstatus')
http = Net::HTTP.new(uri.host, uri.port)
http.use_ssl = true

request = Net::HTTP::Post.new(uri.path)
request['Content-Type'] = 'application/json'
request['Authorization'] = 'Bearer <API Key>'
request.body = {
  messageId: 'message_id'
}.to_json

response = http.request(request)
puts response.body`,
      Go: `package main

import (
	"bytes"
	"fmt"
	"io/ioutil"
	"net/http"
)

func main() {
	client := &http.Client{}

	body := []byte("{\\n    \\"messageId\\": \\"message_id\\",\\n}")

	req, _ := http.NewRequest("POST", "https://api.txtconnect.net/dev/api/sms/getstatus", bytes.NewBuffer(body))

	req.Header.Add("Content-Type", "application/json")
	req.Header.Add("Authorization", "Bearer <API Key>")

	resp, err := client.Do(req)

	if err != nil {
		fmt.Println("Errored when sending request to the server")
		return
	}

	defer resp.Body.Close()
	resp_body, _ := ioutil.ReadAll(resp.Body)

	fmt.Println(resp.Status)
	fmt.Println(string(resp_body))
}`,
      'C#': `//Common testing requirement. If you are consuming an API in a sandbox/test region, uncomment this line of code ONLY for non production uses.
//System.Net.ServicePointManager.ServerCertificateValidationCallback = delegate { return true; };

//Be sure to run "Install-Package Microsoft.Net.Http" from your nuget command line.
using System;
using System.Net.Http;
using System.Text;

class Program
{
    static async System.Threading.Tasks.Task Main()
    {
        using (var client = new HttpClient())
        {
            client.BaseAddress = new Uri("https://api.txtconnect.net/dev/api/sms/");
            client.DefaultRequestHeaders.Authorization = 
                new System.Net.Http.Headers.AuthenticationHeaderValue("Bearer", "<API Key>");
            
            var content = new StringContent(
                "{\\"messageId\\": \\"message_id\\"}",
                Encoding.UTF8,
                "application/json");
            
            var response = await client.PostAsync("getstatus", content);
            var result = await response.Content.ReadAsStringAsync();
            Console.WriteLine(result);
        }
    }
}`,
      'Visual Basic': `Dim request = TryCast(System.Net.WebRequest.Create("https://api.txtconnect.net/dev/api/sms/getstatus"), System.Net.HttpWebRequest)

request.Method = "POST"

request.ContentType = "application/json"
request.Headers.Add("authorization", "Bearer <API Key>")

Using writer = New System.IO.StreamWriter(request.GetRequestStream())
  Dim byteArray As Byte() = System.Text.Encoding.UTF8.GetBytes("{
    \\"messageId\\": \\"message_id\\",
}")
  request.ContentLength = byteArray.Length
  writer.Write(byteArray)
  writer.Close()
End Using
Dim responseContent As String
Using response = TryCast(request.GetResponse(), System.Net.HttpWebResponse)
  Using reader = New System.IO.StreamReader(response.GetResponseStream())
    responseContent = reader.ReadToEnd()
  End Using
End Using`,
      Groovy: `import groovyx.net.http.RESTClient
import static groovyx.net.http.ContentType.JSON
import groovy.json.JsonSlurper
import groovy.json.JsonOutput

@Grab (group = 'org.codehaus.groovy.modules.http-builder', module = 'http-builder', version = '0.5.0')
def client = new RESTClient("https://api.txtconnect.net/dev/api/sms")

def headers = [:]
headers."Content-Type" = "application/json"
headers."Authorization" = "Bearer <API Key>"

def jsonObj = [
    messageId: "message_id"
]

response = client.post( path : "/getstatus",
                        body : jsonObj,
                        headers: headers,
                        contentType : JSON )

println("Status:" + response.status)
println("Body:\\n" + JsonOutput.prettyPrint(JsonOutput.toJson(response.data)))`,
      'Objective-C': `NSURL *URL = [NSURL URLWithString:@"https://api.txtconnect.net/dev/api/sms/getstatus"];

NSMutableURLRequest *request = [NSMutableURLRequest requestWithURL:URL];
[request setHTTPMethod:@"POST"];

[request setValue:@"application/json" forHTTPHeaderField:@"Content-Type"];
[request setValue:@"Bearer <API Key>" forHTTPHeaderField:@"Authorization"];

[request setHTTPBody:[@"{\\n    \\"messageId\\": \\"message_id\\",\\n}" dataUsingEncoding:NSUTF8StringEncoding]];

NSURLSession *session = [NSURLSession sharedSession];
NSURLSessionDataTask *task = [session dataTaskWithRequest:request
                                        completionHandler:
                              ^(NSData *data, NSURLResponse *response, NSError *error) {
                                  if (error) {
                                      NSLog(@"Error: %@", error);
                                      return;
                                  }
                                  
                                  if ([response isKindOfClass:[NSHTTPURLResponse class]]) {
                                      NSLog(@"Response HTTP Status code: %ld\\n", (long)[(NSHTTPURLResponse *)response statusCode]);
                                  }
                                  
                                  NSString* body = [[NSString alloc] initWithData:data encoding:NSUTF8StringEncoding];
                                  NSLog(@"Response Body:\\n%@\\n", body);
                              }];
[task resume];`,
      Swift: `import Foundation

let url = URL(string: "https://api.txtconnect.net/dev/api/sms/getstatus")!
var request = URLRequest(url: url)
request.httpMethod = "POST"
request.addValue("application/json", forHTTPHeaderField: "Content-Type")
request.addValue("Bearer <API Key>", forHTTPHeaderField: "Authorization")

let json: [String: Any] = [
    "messageId": "message_id"
]

request.httpBody = try? JSONSerialization.data(withJSONObject: json)

let task = URLSession.shared.dataTask(with: request) { data, response, error in
    if let error = error {
        print("Error: \\(error)")
        return
    }
    
    if let httpResponse = response as? HTTPURLResponse {
        print("Status code: \\(httpResponse.statusCode)")
    }
    
    if let data = data, let body = String(data: data, encoding: .utf8) {
        print("Response: \\(body)")
    }
}

task.resume()`
    }
  };

  const apiData = {
    'send-sms-post': {
      title: 'Send Single SMS using POST method',
      method: 'POST',
      endpoint: 'https://api.txtconnect.net/dev/api/sms/send',
      description: 'Send a single SMS message using the POST method. This method is more secure and suitable for longer messages as parameters are sent in the request body.',
      parameters: [
        { name: 'Authorization', type: 'header', required: true, description: 'Your API key in the Authorization header' },
        { name: 'to', type: 'string', required: true, description: 'Recipient phone number' },
        { name: 'from', type: 'string', required: true, description: 'Sender name' },
        { name: 'unicode', type: 'string', required: true, description: 'Type of SMS (unicode or regular)' },
        { name: 'sms', type: 'string', required: true, description: 'SMS message content' },
      ],
      responses: [
        { status: 200, description: 'OK', color: 'green' },
        { status: 400, description: 'Bad request', color: 'red' },
        { status: 401, description: 'Unauthorized request', color: 'red' },
        { status: 403, description: 'Forbidden', color: 'red' },
      ],
      responseExample: `
      
   

{
    "messageId": "RRTAPPLMUZRX",
    "msg": "Sms send Successful",
    "data": {
        "status_code": "000",
        "message": "Sms send Successful",
        "in_error": false,
        "reason": "Sms send Successful",
        "data": [],
        "point_in_time": "2026-02-12T14:15:02.228024Z"
    }
}`
    },
    'send-sms-get': {
      title: 'Send Single SMS using GET method',
      method: 'GET',
      endpoint: 'https://api.txtconnect.net/dev/api/sms/send/{to}/{from}/{unicode}/{sms}',
      description: 'Send a single SMS message using the GET method. This is suitable for simple SMS sending with all parameters passed in the URL path.',
      parameters: [
        { name: 'Authorization', type: 'header', required: true, description: 'Your API key in the Authorization header' },
        { name: 'to', type: 'path', required: true, description: 'Recipient phone number' },
        { name: 'from', type: 'path', required: true, description: 'Sender name' },
        { name: 'unicode', type: 'path', required: true, description: 'Type of SMS (unicode or regular)' },
        { name: 'sms', type: 'path', required: true, description: 'SMS message content' },
      ],
      responses: [
        { status: 200, description: 'OK', color: 'green' },
        { status: 400, description: 'Bad request', color: 'red' },
        { status: 401, description: 'Unauthorized request', color: 'red' },
        { status: 403, description: 'Forbidden', color: 'red' },
      ],
      responseExample: `

{
    "messageId": "DL0AXEFPFVVX",
    "msg": "Sms send Successful",
    "data": {
        "status_code": "000",
        "message": "Sms send Successful",
        "in_error": false,
        "reason": "Sms send Successful",
        "data": [],
        "point_in_time": "2026-02-12T14:14:49.888178Z"
    }
}`
    },
    'check-account-balance': {
      title: 'Check Account Balance',
      method: 'GET',
      endpoint: 'https://api.txtconnect.net/dev/api/sms/checkbalance',
      description: 'Check your current account balance and available SMS credits.',
      parameters: [
        { name: 'Authorization', type: 'header', required: true, description: 'Your API key in the Authorization header' },
      ],
      responses: [
        { status: 200, description: 'OK', color: 'green' },
        { status: 401, description: 'Unauthorized request', color: 'red' },
        { status: 403, description: 'Forbidden', color: 'red' },
      ],
      responseExample: `

{
    "data": {
        "status_code": "000",
        "message": "Action Successful",
        "in_error": false,
        "reason": "SMS units retrieved successfully",
        "data": {
            "sms": 904
        },
        "point_in_time": "2026-02-12T14:20:47.037781Z"
    }
}`
    },
    'check-sent-status-get': {
      title: 'Check SMS Delivery Status using GET method',
      method: 'GET',
      endpoint: 'https://api.txtconnect.net/dev/api/sms/getstatus/{msgId}',
      description: 'Check the delivery status of your sent messages using the GET method.',
      parameters: [
        { name: 'Authorization', type: 'header', required: true, description: 'Your API key in the Authorization header' },
        { name: 'msgId', type: 'path', required: true, description: 'Unique message ID for which a report is requested' },
      ],
      responses: [
        { status: 200, description: 'OK', color: 'green' },
        { status: 400, description: 'Bad request', color: 'red' },
        { status: 401, description: 'Unauthorized request', color: 'red' },
      ],
      responseExample: `{
  "msg": "Message delivered"
}`
    },
    'check-delivery-status-post': {
      title: 'Check SMS Delivery Status using POST method',
      method: 'POST',
      endpoint: 'https://api.txtconnect.net/dev/api/sms/getstatus',
      description: 'Check the delivery status of your sent messages using this POST endpoint.',
      parameters: [
        { name: 'Authorization', type: 'header', required: true, description: 'The API key' },
        { name: 'messageId', type: 'string', required: true, description: 'Unique message ID for which a report is requested' },
      ],
      responses: [
        { status: 200, description: 'OK', color: 'green' },
        { status: 400, description: 'Bad request', color: 'red' },
        { status: 401, description: 'Unauthorized request', color: 'red' },
      ],
      responseExample: `{
  "msg": "Message delivered"
}`
    },
  };

  // Get current section data with fallback
  const getCurrentSectionData = () => {
    const section = sections.find(s => s.id === selectedSection);
    if (!section || section.type === 'header') {
      return apiData['send-sms-post'];
    }
    return apiData[selectedSection] || apiData['send-sms-post'];
  };

  const currentSection = getCurrentSectionData();
  
  const getCurrentCode = () => {
    const sectionCode = codeExamples[selectedSection];
    if (!sectionCode) return `// Code example not available for ${selectedSection}`;
    
    const langCode = sectionCode[selectedLanguage];
    if (!langCode) return `// ${selectedLanguage} code example not available for this endpoint`;
    
    return langCode;
  };

  const getEndpointUrl = () => {
    return currentSection.endpoint;
  };

  const getMethodColor = (method) => {
    switch (method) {
      case 'GET': return 'bg-blue-100 text-blue-700';
      case 'POST': return 'bg-orange-100 text-orange-700';
      case 'PUT': return 'bg-yellow-100 text-yellow-700';
      case 'DELETE': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const copyToClipboard = () => {
    const code = getCurrentCode();
    navigator.clipboard.writeText(code)
      .then(() => {
        alert('Code copied to clipboard!');
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      <div className="p-4 lg:p-6 flex items-center justify-between lg:block border-b border-gray-200 flex-shrink-0">
        <div>
          <h2 className="text-lg lg:text-xl font-bold text-gray-900 mb-1">TxtConnect APIs</h2>
          <p className="text-xs lg:text-sm text-gray-500">Production Environment</p>
        </div>
        <button
          type="button"
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <nav className="px-4 pb-6 pt-2 overflow-y-auto flex-1">
        {sections.map((section) => (
          section.type === 'header' ? (
            <div key={section.id} className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {section.name}
            </div>
          ) : (
            <button
              key={section.id}
              onClick={() => {
                setSelectedSection(section.id);
                setSidebarOpen(false);
              }}
              className={`w-full text-left px-4 py-2.5 mb-1 rounded-lg transition-colors text-sm ${
                selectedSection === section.id
                  ? 'bg-teal-50 text-teal-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {section.name}
            </button>
          )
        ))}
      </nav>
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Navbar: auth vs guest */}
      {isAuthenticated ? <Navbar /> : <TopNavbar />}

      <div className="flex-1 min-h-0 flex relative pt-16">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-20 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setSidebarOpen(false)}
            role="button"
            tabIndex={0}
            aria-label="Close sidebar"
          />
        )}

        {/* Sidebar: fixed directly under navbar on desktop, drawer on mobile */}
        <aside
          className={`
            fixed top-16 left-0 bottom-0 z-30 w-72 lg:w-64
            bg-white border-r border-gray-200
            transform transition-transform duration-200 ease-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
        >
          {sidebarContent}
        </aside>

        {/* Main Content - only this area scrolls; ml-64 reserves space for fixed sidebar on desktop */}
        <div className="flex-1 min-w-0 min-h-0 overflow-y-auto lg:ml-64">
          {/* Mobile sidebar toggle */}
          <div className="sticky top-0 z-10 lg:hidden flex items-center gap-2 bg-gray-50 border-b border-gray-200 px-4 py-3">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-100"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5 text-gray-700" />
            </button>
            <span className="text-sm font-medium text-gray-700">API Sections</span>
          </div>

          <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6 lg:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-4">
              <span className={`inline-flex w-fit px-3 py-1 rounded-md text-sm font-medium ${getMethodColor(currentSection.method)}`}>
                {currentSection.method}
              </span>
              <code className="text-xs sm:text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded break-all">
                {getEndpointUrl()}
              </code>
            </div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-3">
              {currentSection.title}
            </h1>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
              {currentSection.description}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Left Column - Parameters */}
            <div className="min-w-0">
              <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 mb-4 lg:mb-6">
                <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-4">Parameters</h3>
                <div className="space-y-4">
                  {currentSection.parameters.map((param, index) => (
                    <div key={index} className="border-b border-gray-100 pb-4 last:border-0">
                      <div className="flex items-center gap-2 mb-1">
                        <code className="text-sm font-medium text-teal-600">{param.name}</code>
                        {param.required && (
                          <span className="text-xs text-red-500">required</span>
                        )}
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                          {param.type}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mt-1">{param.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Responses */}
              <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
                <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-4">Responses</h3>
                <div className="space-y-2">
                  {currentSection.responses.map((response, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          response.color === 'green' ? 'bg-green-500' : 'bg-red-500'
                        }`} />
                        <span className="font-medium text-gray-900">{response.status}</span>
                        <span className="text-gray-600">{response.description}</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Code Example */}
            <div className="min-w-0">
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {/* Dropdowns */}
                <div className="border-b border-gray-200 p-3 sm:p-4">
                  <div className="flex items-center justify-between gap-4">
                    {/* Language Dropdown */}
                    <div className="relative w-full min-w-0">
                      <button
                        onClick={() => setShowLangDropdown(!showLangDropdown)}
                        className="w-full px-3 sm:px-4 py-2 text-left bg-gray-50 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-between min-w-0"
                      >
                        <span className="text-sm font-medium text-gray-700 truncate">{selectedLanguage}</span>
                        <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0 ml-2" />
                      </button>
                      {showLangDropdown && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20 max-h-64 overflow-y-auto">
                          {languages.map((lang) => (
                            <button
                              key={lang}
                              onClick={() => {
                                setSelectedLanguage(lang);
                                setShowLangDropdown(false);
                              }}
                              className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                                selectedLanguage === lang
                                  ? 'bg-teal-50 text-teal-700 font-medium'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {lang}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Code Block */}
                <div className="bg-gray-900 p-3 sm:p-6 relative overflow-hidden">
                  <button 
                    onClick={copyToClipboard}
                    className="absolute top-2 right-2 sm:top-4 sm:right-4 p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors z-10"
                    title="Copy to clipboard"
                  >
                    <Copy className="w-4 h-4 text-gray-300" />
                  </button>
                  <pre className="text-xs sm:text-sm text-gray-100 overflow-x-auto whitespace-pre-wrap pr-12 sm:pr-14 max-h-[min(50vh,400px)] sm:max-h-none overflow-y-auto">
                    <code>{getCurrentCode()}</code>
                  </pre>
                </div>

                {/* Response Preview */}
                <div className="p-3 sm:p-6 border-t border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="text-sm font-medium text-gray-700">200</span>
                    <span className="text-sm text-gray-500">Example Response</span>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-3 sm:p-4 overflow-x-auto">
                    <pre className="text-xs sm:text-sm text-gray-100 overflow-x-auto whitespace-pre-wrap">
                      <code>{currentSection.responseExample}</code>
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default ApiDocumentation;